import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Modal from '../../../components/Common/modal/Modal';
import ClassroomAddNewItems from './ClassroomAddNewItem';
import ClassroomAddNewProject from './ClassroomAddNewProject';
import { createUserClassroom } from '../services/Teacher.service';
import { AppDispatch } from '../../../store/store';
import { UserLocalStorageKeysEnum } from '../../../interfaces/user.interface';
import { setActiveClassroom, setActiveClassroomProject } from '../../../store/userSlice';
import { getTeacherClassrooms } from '../../../store/thunks/user-teacher.thunk';

export interface ClassroomSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateComplete: (classroomId?: string, projectId?: string) => void;
  isFirstClassroom?: boolean;
}

type WizardStep = 'classroom' | 'project';

const ClassroomSetupWizard: React.FC<ClassroomSetupWizardProps> = ({
  isOpen,
  onClose,
  onCreateComplete,
  isFirstClassroom = false,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [createClassroomLoader, setCreateClassroomLoader] = useState(false);
  const [step, setStep] = useState<WizardStep>('classroom');
  const [classroomName, setClassroomName] = useState('');

  const handleClassroomNameSubmit = (name: string) => {
    setClassroomName(name);
    setStep('project');
  };

  const handleCreateClassroom = async (projectName: string, projectTemplateId?: string) => {
    if (createClassroomLoader) return;
    setCreateClassroomLoader(true);
    try {
      await createUserClassroom(
        classroomName,
        (response) => {
          if (response?.classroom?.id && response?.project?.id) {
            localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID, response.classroom.id);
            localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID, response.project.id);
            dispatch(setActiveClassroom({ classroom: response.classroom }));
            dispatch(setActiveClassroomProject({ project: response.project }));
            dispatch(getTeacherClassrooms());
            onClose();
            onCreateComplete(response.classroom.id, response.project.id);
          }
        },
        { projectName, projectTemplateId },
      );
    } catch (err) {
      console.error(err);
    } finally {
      setCreateClassroomLoader(false);
    }
  };

  const handleClose = () => {
    setStep('classroom');
    setClassroomName('');
    onClose();
  };

  if (!isOpen) return null;

  const modalProps =
    step === 'classroom'
      ? {
          title: 'Create a new classroom',
          description: "A classroom is a digital space for you to track your students' books.",
        }
      : {
          title: 'Set up your first Project',
          description: 'Choose a project to get started. Your class timeline starts after you pick a start date in the next step.',
        };

  return (
    <Modal
      title={modalProps.title}
      description={modalProps.description}
      className="global-absolute-popup-style font-sofiaSans content-left secondary-small"
      mainContainerClassName="sm:max-w-[430px]"
      descriptionClassContainer="md:w-full"
      descriptionClassName="text-storyblackText font-sofiaSans font-medium"
      showSubmitButton={false}
      showCloseButton={false}
      showTopRightCloseButton={!isFirstClassroom}
      showCloseButtonFunction={handleClose}
    >
      {step === 'classroom' && (
        <ClassroomAddNewItems
          loader={false}
          isOnboarding={isFirstClassroom}
          close={isFirstClassroom ? undefined : handleClose}
          back={isFirstClassroom ? undefined : handleClose}
          submit={handleClassroomNameSubmit}
        />
      )}
      {step === 'project' && (
        <ClassroomAddNewProject
          loader={createClassroomLoader}
          isOnboarding={isFirstClassroom}
          back={() => setStep('classroom')}
          submit={handleCreateClassroom}
        />
      )}
    </Modal>
  );
};

export default ClassroomSetupWizard;
